import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getCheckoutCart } from '../api/botApi';
import {
  setWholeCart,
  ShoppingCartV2,
} from '../../../modules/ShoppingCart/ShoppingCartSliceV2';
import { generateUUID } from '../../../modules/ShoppingCart/hooks/useHandleCheckout';

/** Mirrors ToolExecutorService.CHECKOUT_URL_TTL_MS on the BE. */
const BOT_CHECKOUT_TTL_MS = 30 * 60 * 1000;
const BOT_CHECKOUT_INDEX_KEY = 'bot_checkout_sessions';

interface BotCheckoutSessionEntry {
  uuid: string;
  expiresAt: number;
}

function readCheckoutIndex(): Record<string, BotCheckoutSessionEntry> {
  try {
    return JSON.parse(localStorage.getItem(BOT_CHECKOUT_INDEX_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function saveCheckoutIndex(sessionId: string, uuid: string, expiresAtMs: number) {
  const index = readCheckoutIndex();
  index[sessionId] = { uuid, expiresAt: expiresAtMs };
  localStorage.setItem(BOT_CHECKOUT_INDEX_KEY, JSON.stringify(index));
}

function findValidCachedCheckout(
  sessionId: string,
): { uuid: string; cart: ShoppingCartV2 } | null {
  const cartV2All = JSON.parse(localStorage.getItem('cartV2') ?? '{}');
  const cartAll = JSON.parse(localStorage.getItem('cart') ?? '{}');

  const tryUuid = (uuid: string): { uuid: string; cart: ShoppingCartV2 } | null => {
    const cart = cartV2All[uuid] as ShoppingCartV2 | undefined;
    if (!cart || !Array.isArray(cart.products) || !cart.products.length) {
      return null;
    }
    if (!cartAll[uuid]) return null;
    return { uuid, cart };
  };

  const indexEntry = readCheckoutIndex()[sessionId];
  if (indexEntry && Date.now() <= indexEntry.expiresAt) {
    const hit = tryUuid(indexEntry.uuid);
    if (hit) return hit;
  }

  // Legacy fallback for carts stashed before the session index existed.
  for (const [uuid, rawCart] of Object.entries(cartV2All)) {
    const cart = rawCart as ShoppingCartV2;
    if (cart?.bot_session_id !== sessionId) continue;
    const cartTime = (cartAll[uuid] as { time?: number } | undefined)?.time ?? 0;
    if (Date.now() - cartTime > BOT_CHECKOUT_TTL_MS) continue;
    const hit = tryUuid(uuid);
    if (hit) return hit;
  }

  return null;
}

/**
 * Single web checkout handler for the bot.
 *
 * The backend (BotCartBuilderService) now assembles the full ShoppingCartV2 for
 * every supported product type (hotel + flight bundle), so the FE no longer
 * re-runs per-product reducers. Flow:
 *   1. GET /bot-agent/sessions/:id/checkout-cart → full V2 cart.
 *   2. Mint a checkout uuid (same `t<ts>_<rand>` format as useHandleCheckout).
 *   3. Write BOTH localStorage keys the Checkout page reads:
 *        - cart[uuid]   = { shoppingCart, settings, time }  (the bounce-guard
 *          in Checkout.tsx navigates home if cart[uuid] is missing)
 *        - cartV2[uuid] = the V2 cart (the actual render source).
 *   4. dispatch(setWholeCart) so the live store matches the stashed cart.
 *   5. navigate('/checkout/<uuid>').
 *
 * `bot_session_id` is stamped on the cart for back-office order↔chat linkage
 * (the BE builder intentionally omits it — see the cart fixtures contract).
 *
 * This is the single web-checkout entry point, shared by:
 *   - the chat CTA button + CheckoutPanel (web, no token), and
 *   - the WhatsApp deep-link landing (`useBotSessionHydration`, which passes the
 *     signed `token` from the URL).
 */
export function useBotCheckoutHandler(): (
  sessionId: string,
  token?: string,
) => Promise<void> {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return useCallback(
    async (sessionId: string, token?: string) => {
      const cachedCheckout = !token ? findValidCachedCheckout(sessionId) : null;

      if (cachedCheckout) {
        const cartForReuse: ShoppingCartV2 = {
          ...cachedCheckout.cart,
          bot_session_id: sessionId,
        };
        dispatch(setWholeCart(cartForReuse));
        navigate(`/checkout/${cachedCheckout.uuid}`);
        return;
      }

      const cart = await getCheckoutCart(sessionId, token);
      if (!cart || !Array.isArray(cart.products) || !cart.products.length) {
        // eslint-disable-next-line no-console
        console.warn(
          '[useBotCheckoutHandler] empty/invalid checkout cart for session',
          sessionId,
        );
        return;
      }

      const cartForStash: ShoppingCartV2 = {
        ...(cart as ShoppingCartV2),
        // Linkage for order creation (CheckoutApis forwards this to the BE).
        bot_session_id: sessionId,
      } as ShoppingCartV2;

      const uuid = generateUUID();

      // cart[uuid] — Checkout.tsx bounces home if this is missing while a
      // cartV2 entry exists. Mirror the shape useHandleCheckout writes.
      const prevCart = JSON.parse(localStorage.getItem('cart') ?? '{}');
      localStorage.setItem(
        'cart',
        JSON.stringify({
          ...prevCart,
          [uuid]: {
            shoppingCart: cartForStash,
            settings: cart.settings ?? { show_guest_passport: true },
            time: Date.now(),
          },
        }),
      );

      // cartV2[uuid] — the render source for /checkout/:uuid.
      const prevCartV2 = JSON.parse(localStorage.getItem('cartV2') ?? '{}');
      localStorage.setItem(
        'cartV2',
        JSON.stringify({ ...prevCartV2, [uuid]: cartForStash }),
      );

      saveCheckoutIndex(sessionId, uuid, Date.now() + BOT_CHECKOUT_TTL_MS);

      dispatch(setWholeCart(cartForStash));
      navigate(`/checkout/${uuid}`);
    },
    [navigate, dispatch],
  );
}

/**
 * Pull `bot_session_id` out of a path like `/shopping-cart?bot_session_id=<uuid>`.
 * Returns undefined for anything that doesn't carry the param.
 */
export function extractBotSessionId(url: string): string | undefined {
  const m = url.match(/[?&]bot_session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : undefined;
}
